import * as msal from '@azure/msal-browser';
import { TenantConfig } from '@/config/tenantConfig';
import { config } from '@/config/config';
import { ConnectClaim } from './claims';

class AuthenticationService {
  private app: msal.PublicClientApplication;
  private tenantConfig: TenantConfig;

  private account: msal.AccountInfo | null;
  private idToken: string;
  private scopes: string[];
  private signInUrl: string;
  private passwordResetUrl: string;

  public setup() {
    this.tenantConfig = config().tenantConfig;

    this.signInUrl =
      `${this.tenantConfig.authority}${this.tenantConfig.tenant}/${this.tenantConfig.signInPolicy}`.toLowerCase();
    this.passwordResetUrl =
      `${this.tenantConfig.authority}/${this.tenantConfig.tenant}/${this.tenantConfig.passwordResetPolicy}`.toLowerCase();

    this.scopes = [this.tenantConfig.clientId]; // using the clientId as the scope grants us access to our web api's
    this.account = null;
    this.idToken = '';

    const conf: msal.Configuration = {
      auth: {
        clientId: this.tenantConfig.clientId,
        navigateToLoginRequestUrl: false,
        redirectUri: this.tenantConfig.redirectUri,
        authority: this.signInUrl,
        knownAuthorities: [this.signInUrl, this.passwordResetUrl],
      },
      cache: {
        cacheLocation: 'localStorage',
      },
      system: {
        loadFrameTimeout: 2000,
      },
    };

    this.app = new msal.PublicClientApplication(conf);
  }

  public async load() {
    try {
      const response = await this.app.handleRedirectPromise();
      await this.handleRedirectResponse(response);
    } catch (error) {
      await this.handleRedirectError(error);
    }
  }

  public login() {
    return this.app.loginRedirect({ scopes: this.scopes });
  }

  public logout() {
    let account: msal.AccountInfo | undefined;
    if (this.account) {
      account = this.account;
    }
    const logOutRequest: msal.EndSessionRequest = {
      account,
      idTokenHint: this.idToken,
      postLogoutRedirectUri: this.tenantConfig.redirectUri,
    };

    // clear the local vars
    this.idToken = '';
    this.account = null;

    return this.app.logoutRedirect(logOutRequest);
  }

  public async getToken() {
    const account = this.account ?? this.app.getAllAccounts()[0];

    try {
      const response = await this.app.acquireTokenSilent({ account, scopes: this.scopes });
      this.idToken = response.idToken;
      return response.accessToken;
    } catch (e) {
      await this.app.acquireTokenRedirect({ account, scopes: this.scopes });
    }

    return null;
  }

  public isAuthenticated() {
    return !!this.account;
  }

  public isConnectUser() {
    return !!this.getClaim(ConnectClaim.UserId);
  }

  public getUserName(): string {
    return this.account?.name ?? '';
  }

  public getClaim(key: ConnectClaim) {
    return (this.account?.idTokenClaims as any)[key] ?? '';
  }

  private async handleRedirectResponse(response: msal.AuthenticationResult | null) {
    if (!response) {
      const accounts = this.app.getAllAccounts();
      if (accounts && accounts.length > 0) {
        // there shouldn't be more than one, but if there is pick the first one
        this.account = accounts[0];
      }
    } else if (response.authority.startsWith(this.passwordResetUrl)) {
      await this.logout();
    } else {
      this.idToken = response.idToken;
      this.account = response.account;
    }
  }

  private async handleRedirectError(error: any) {
    if (error instanceof msal.AuthError) {
      if (error.errorMessage.includes('AADB2C90091')) {
        // user cancelled password reset, redirect to signin policy
        await this.app.loginRedirect({ scopes: this.scopes });
      }
      if (error.errorMessage.includes('AADB2C90118')) {
        this.app.getConfiguration().auth.authority = this.passwordResetUrl;
        await this.app.loginRedirect({ scopes: this.scopes });
      }
    }
  }
}

const authService = new AuthenticationService();
export default authService;
